/**
 * Premium Badge Component
 *
 * 유료 사용자와 무료 사용자를 구분해서 보여주는 배지 컴포넌트
 */
import { Crown, Zap } from "lucide-react";

import { Badge } from "./ui/badge";
import { Button } from "./ui/button";

interface PremiumBadgeProps {
  isPaidUser: boolean;
  variant?: "badge" | "card" | "inline";
  showUpgradeButton?: boolean;
  className?: string;
}

export function PremiumBadge({
  isPaidUser,
  variant = "badge",
  showUpgradeButton = false,
  className = "",
}: PremiumBadgeProps) {
  if (variant === "badge") {
    return isPaidUser ? (
      <Badge
        variant="outline"
        className={`border-yellow-300 bg-yellow-50 text-yellow-800 ${className}`}
      >
        <Crown className="mr-1 h-3 w-3" />
        프리미엄
      </Badge>
    ) : (
      <Badge variant="secondary" className={`${className}`}>
        무료 사용자
      </Badge>
    );
  }

  if (variant === "card") {
    return (
      <div
        className={`rounded-lg border p-4 ${
          isPaidUser
            ? "border-yellow-200 bg-yellow-50"
            : "border-border bg-secondary"
        } ${className}`}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            {isPaidUser ? (
              <>
                <Crown className="h-5 w-5 text-yellow-600" />
                <div>
                  <h3 className="font-semibold text-yellow-800">
                    프리미엄 사용자
                  </h3>
                  <p className="text-sm text-yellow-600">
                    무제한 AI 피드백 이용 가능! 🎉
                  </p>
                </div>
              </>
            ) : (
              <>
                <Zap className="h-5 w-5 text-muted-foreground" />
                <div>
                  <h3 className="font-semibold text-foreground">무료 사용자</h3>
                  <p className="text-sm text-muted-foreground">
                    하루 3회까지 AI 피드백 이용 가능
                  </p>
                </div>
              </>
            )}
          </div>
          {!isPaidUser && showUpgradeButton && (
            <Button
              asChild
              size="sm"
              className="bg-primary text-primary-foreground hover:brightness-95"
            >
              <a href="/payments/checkout">
                <Crown className="mr-1 h-3 w-3" />
                업그레이드
              </a>
            </Button>
          )}
        </div>
      </div>
    );
  }

  if (variant === "inline") {
    return (
      <span
        className={`inline-flex items-center gap-1 ${
          isPaidUser ? "text-yellow-600" : "text-muted-foreground"
        } ${className}`}
      >
        {isPaidUser ? (
          <>
            <Crown className="h-4 w-4" />
            <span className="text-sm font-medium">프리미엄</span>
          </>
        ) : (
          <>
            <Zap className="h-4 w-4" />
            <span className="text-sm">무료</span>
          </>
        )}
      </span>
    );
  }

  return null;
}

/**
 * Usage Display Component
 *
 * 사용량을 표시하는 컴포넌트
 */
interface UsageDisplayProps {
  used: number;
  isPaidUser: boolean;
  className?: string;
}

export function UsageDisplay({
  used,
  isPaidUser,
  className = "",
}: UsageDisplayProps) {
  return (
    <div
      className={`flex items-center gap-2 text-sm ${
        isPaidUser ? "text-yellow-600" : "text-muted-foreground"
      } ${className}`}
    >
      {isPaidUser ? (
        <>
          <Crown className="h-4 w-4" />
          <span className="font-medium">무제한 사용 가능</span>
        </>
      ) : (
        <>
          <Zap className="h-4 w-4" />
          <span>
            오늘 <span className="font-medium">{used}/3</span>회 사용
          </span>
        </>
      )}
    </div>
  );
}

/**
 * Upgrade Prompt Component
 *
 * 무료 사용자에게 업그레이드를 유도하는 컴포넌트
 */
interface UpgradePromptProps {
  usedSessions: number;
  className?: string;
}

export function UpgradePrompt({
  usedSessions,
  className = "",
}: UpgradePromptProps) {
  const remainingSessions = Math.max(0, 3 - usedSessions);
  const isLimitReached = remainingSessions === 0;

  return (
    <div
      className={`rounded-lg border border-border bg-accent p-4 ${className}`}
    >
      <div className="flex items-start gap-3">
        <Crown className="mt-0.5 h-5 w-5 flex-shrink-0 text-primary" />
        <div className="flex-1">
          <h3 className="mb-1 font-semibold text-foreground">
            {isLimitReached
              ? "오늘의 무료 이용이 끝났어요"
              : `오늘 ${remainingSessions}번 더 이용 가능해요`}
          </h3>
          <p className="mb-3 text-sm text-muted-foreground">
            프리미엄으로 업그레이드하면 <strong>무제한</strong>으로 AI 동생과
            대화할 수 있어요!
          </p>
          <div className="mb-4 space-y-2 text-sm text-muted-foreground">
            <div className="flex items-center gap-2">
              <span className="h-1 w-1 rounded-full bg-primary"></span>
              <span>무제한 AI 피드백</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="h-1 w-1 rounded-full bg-primary"></span>
              <span>고품질 음성 인식</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="h-1 w-1 rounded-full bg-primary"></span>
              <span>학습 진도 상세 분석</span>
            </div>
          </div>
          <Button
            asChild
            className="w-full bg-primary text-primary-foreground hover:brightness-95"
          >
            <a href="/payments/checkout">
              <Crown className="mr-2 h-4 w-4" />월 9,900원으로 프리미엄 시작하기
            </a>
          </Button>
        </div>
      </div>
    </div>
  );
}
